import { createApi, BaseQueryFn } from '@reduxjs/toolkit/query/react';
import { createEntityAdapter } from '@reduxjs/toolkit';
import {
  INoteBook,
  INoteBookNormalizedResponse,
  INoteBookQueryParam,
  TNotebookRawResponse,
  ICreateNoteBook,
  IUpdateNoteBook,
} from './typings';
import { QUERIES } from './constants';

export const notebookAdapter = createEntityAdapter({
  selectId: (notebook: INoteBook) => notebook.id,
  // sortComparer: (a, b) => {
  //   if (a.pinned_at && !b.pinned_at) return -1;
  //   if (!a.pinned_at && b.pinned_at) return 1;
  //   if (a.pinned_at && b.pinned_at) {
  //     return new Date(b.pinned_at).getTime() - new Date(a.pinned_at).getTime();
  //   }

  //   const getValidDate = (item: INoteBook) => {
  //     const date = item.created_at || item.date_of_publish;
  //     return date ? new Date(date).getTime() : 0;
  //   };

  //   const dateA = getValidDate(a);
  //   const dateB = getValidDate(b);

  //   return dateB - dateA;
  // },
});

export const notebookSelector = notebookAdapter.getSelectors();

export const notebookApiService = (baseQuery: BaseQueryFn) => {
  const api = createApi({
    reducerPath: 'notebook-api',
    baseQuery,
    tagTypes: [QUERIES.exclusiveContents],
    endpoints: (builder) => ({
      getNotebooks: builder.query<INoteBookNormalizedResponse, INoteBookQueryParam>({
        query: ({ ...rest }) => ({
          url: 'public/v1/exclusive_content/notebook',
          params: rest,
        }),
        providesTags: [QUERIES.exclusiveContents],
        transformResponse: (response: TNotebookRawResponse) => {
          const initialState = notebookAdapter.getInitialState();
          return {
            notebooks: notebookAdapter.setAll(initialState, response.notebooks),
            total_pages: response.total_pages,
            total_records: response.total_records,
          };
        },
        forceRefetch: ({ currentArg, previousArg }) => {
          const isSamePage = currentArg?.page === previousArg?.page;
          const isSameParentNotebookId =
            currentArg?.parent_notebook_id === previousArg?.parent_notebook_id;
          const isSameStatus = currentArg?.status === previousArg?.status;
          return !isSamePage || !isSameParentNotebookId || !isSameStatus;
        },
        serializeQueryArgs: ({ endpointName, queryArgs }) => {
          return `${endpointName}-${queryArgs?.creator_id}-${queryArgs?.status}-${queryArgs?.parent_notebook_id}`;
        },
        merge: (currentState, incomingState) => {
          notebookAdapter.addMany(
            currentState.notebooks,
            notebookSelector.selectAll(incomingState.notebooks),
          );
          currentState.total_pages = incomingState.total_pages;
          currentState.total_records = incomingState.total_records;
        },
      }),
      getFanNotebooks: builder.query<
        INoteBookNormalizedResponse,
        {
          page: number;
          page_size: number;
          creator_id: string;
          fan_id: string;
          visibility?: string;
          parent_notebook_id?: string | null;
        }
      >({
        query: (params) => ({
          url: 'public/v1/exclusive_content/fan_notebooks',
          params,
        }),
        providesTags: [QUERIES.exclusiveContents],
        transformResponse: (response: TNotebookRawResponse) => {
          const initialState = notebookAdapter.getInitialState();
          return {
            notebooks: notebookAdapter.setAll(initialState, response.notebooks),
            total_pages: response.total_pages,
            total_records: response.total_records,
          };
        },
        forceRefetch: ({ currentArg, previousArg }) => {
          const isSamePage = currentArg?.page === previousArg?.page;
          return !isSamePage;
        },
        serializeQueryArgs: ({ endpointName, queryArgs }) => {
          return `${endpointName}-${queryArgs?.creator_id}-${queryArgs?.visibility}-${queryArgs?.parent_notebook_id}`;
        },
        merge: (currentState, incomingState) => {
          notebookAdapter.addMany(
            currentState.notebooks,
            notebookSelector.selectAll(incomingState.notebooks),
          );
          currentState.total_pages = incomingState.total_pages;
          currentState.total_records = incomingState.total_records;
        },
      }),
      getNotebookById: builder.query<INoteBook, string>({
        query: (id) => ({ url: `public/v1/exclusive_content/notebook/${id}` }),
        providesTags: (result, error, id) => [{ type: QUERIES.exclusiveContents, id }],
      }),
      getFanNotebookById: builder.query<
        INoteBook,
        {
          id: string;
          creator_id: string;
          fan_id: string;
        }
      >({
        query: ({ id, creator_id, fan_id }) => ({
          url: `public/v1/exclusive_content/fan_notebook/${id}`,
          params: { creator_id, fan_id },
        }),
        transformResponse: (response: { notebook: INoteBook }) => response.notebook,
      }),
      createNotebook: builder.mutation<INoteBook, ICreateNoteBook>({
        query: (notebookData) => ({
          url: 'public/v1/exclusive_content/notebook',
          method: 'POST',
          body: notebookData,
        }),
        transformResponse: (response: { notebook: INoteBook }) => response.notebook,
        invalidatesTags: [QUERIES.exclusiveContents],
      }),
      updateNotebook: builder.mutation<INoteBook, IUpdateNoteBook>({
        query: ({ id, queryParams, ...rest }) => ({
          url: `public/v1/exclusive_content/notebook/${id}`,
          method: 'PATCH',
          body: rest,
        }),
        transformResponse: (response: { notebook: INoteBook }) => response.notebook,
        // invalidatesTags: [QUERIES.exclusiveContents],
        async onQueryStarted({ id, ...rest }, { dispatch, queryFulfilled }) {
          const patchResult = dispatch(
            api.util.updateQueryData('getNotebooks', rest.queryParams, (draft) => {
              const notebook = notebookSelector.selectById(draft.notebooks, id);
              if (!notebook) return;

              const shouldRemoveFromCurrentQueryList =
                rest.parent_notebook_id !== notebook.parent_notebook_id;

              if (shouldRemoveFromCurrentQueryList) {
                notebookAdapter.removeOne(draft.notebooks, id);
                // add to new list
                const destinationQueryParams = {
                  ...rest.queryParams,
                  parent_notebook_id: rest.parent_notebook_id,
                };
                dispatch(
                  api.util.updateQueryData('getNotebooks', destinationQueryParams, (draft) => {
                    notebookAdapter.addOne(draft.notebooks, {
                      ...notebook,
                      ...rest,
                    });
                  }),
                );

                // update the new parent notebook
                if (rest?.parent_notebook_id) {
                  const parentNotebook = notebookSelector.selectById(
                    draft.notebooks,
                    rest.parent_notebook_id,
                  );
                  notebookAdapter.updateOne(draft.notebooks, {
                    id: rest.parent_notebook_id,
                    changes: {
                      children_count: parentNotebook?.children_count
                        ? parentNotebook.children_count + 1
                        : 1,
                    },
                  });
                }
                // update the old parent notebook
                if (rest?.queryParams?.parent_notebook_id) {
                  const oldParentNotebook = notebookSelector.selectById(
                    draft.notebooks,
                    rest.queryParams.parent_notebook_id,
                  );
                  notebookAdapter.updateOne(draft.notebooks, {
                    id: rest.queryParams.parent_notebook_id,
                    changes: {
                      children_count:
                        oldParentNotebook?.children_count && oldParentNotebook.children_count > 0
                          ? oldParentNotebook.children_count - 1
                          : 0,
                    },
                  });
                }
              }

              // update the notebook
              notebookAdapter.updateOne(draft.notebooks, {
                id,
                changes: {
                  ...notebook,
                  ...rest,
                },
              });
            }),
          );

          try {
            // await queryFulfilled;
          } catch (error) {
            patchResult.undo();
          }
        },
      }),
      deleteNotebook: builder.mutation<
        { success: boolean },
        { id: string; queryParams: INoteBookQueryParam }
      >({
        query: ({ id }) => ({
          url: `public/v1/exclusive_content/notebook/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: (result, error, param) => [
          { type: QUERIES.exclusiveContents, id: param.id },
        ],
        async onQueryStarted({ id, queryParams }, { dispatch, queryFulfilled }) {
          const patchResult = dispatch(
            api.util.updateQueryData('getNotebooks', queryParams, (draft) => {
              notebookAdapter.removeOne(draft.notebooks, id);
            }),
          );

          try {
            await queryFulfilled;
          } catch (error) {
            patchResult.undo();
          }
        },
      }),
      togglePinNotebook: builder.mutation({
        query: ({ id, pin_action }) => ({
          url: `public/v1/exclusive_content/notebooks/${id}/toggle_pin`,
          method: 'POST',
          params: { pin_action },
        }),
        async onQueryStarted({ id, pin_action, queryParams }, { dispatch, queryFulfilled }) {
          let patchResult;
          try {
            patchResult = dispatch(
              api.util.updateQueryData('getNotebooks', queryParams, (draft) => {
                const notebook = notebookSelector.selectById(draft.notebooks, id);
                if (!notebook) return;
                notebookAdapter.updateOne(draft.notebooks, {
                  id,
                  changes: {
                    pinned_at: pin_action === 'pin' ? new Date().toISOString() : null,
                  },
                });
                const allNotebooks = notebookSelector.selectAll(draft.notebooks);

                const sortedNotebooks = [...allNotebooks].sort((a, b) => {
                  if (a.pinned_at && !b.pinned_at) return -1;
                  if (!a.pinned_at && b.pinned_at) return 1;

                  if (a.pinned_at && b.pinned_at) {
                    return new Date(b.pinned_at).getTime() - new Date(a.pinned_at).getTime();
                  }
                  return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
                });

                notebookAdapter.setAll(draft.notebooks, sortedNotebooks);
              }),
            );
            const response = await queryFulfilled;

            dispatch(api.util.invalidateTags(['exclusiveContents']));
          } catch {
            patchResult?.undo();
          }
        },
      }),
      reorderNotebooks: builder.mutation({
        query: ({ creator_id, parent_notebook_id, ordered_items }) => ({
          url: `public/v1/exclusive_content/notebooks/reorder/${creator_id}`,
          method: 'PUT',
          body: {
            parent_notebook_id: parent_notebook_id || null,
            ordered_items,
          },
        }),
        async onQueryStarted(
          { creator_id, parent_notebook_id, ordered_items, queryParams },
          { dispatch, queryFulfilled },
        ) {
          let patchResult;
          try {
            patchResult = dispatch(
              api.util.updateQueryData('getNotebooks', queryParams, (draft) => {
                ordered_items.forEach((item: { id: string; order_index: number }) => {
                  const notebook = notebookSelector.selectById(draft.notebooks, item.id);
                  if (notebook) {
                    notebookAdapter.updateOne(draft.notebooks, {
                      id: item.id,
                      changes: {
                        order_index: item.order_index,
                      },
                    });
                  }
                });

                const allNotebooks = notebookSelector.selectAll(draft.notebooks);

                const pinned = allNotebooks.filter((n) => n.pinned_at);
                const unpinned = allNotebooks.filter((n) => !n.pinned_at);

                const sortedPinned = pinned.sort((a, b) => {
                  if (a.pinned_at && b.pinned_at) {
                    return new Date(b.pinned_at).getTime() - new Date(a.pinned_at).getTime();
                  }
                  return 0;
                });

                const sortedUnpinned = unpinned.sort((a, b) => {
                  const aOrder = a.order_index ?? Infinity;
                  const bOrder = b.order_index ?? Infinity;
                  if (aOrder !== bOrder) return aOrder - bOrder;
                  return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
                });

                const sortedNotebooks = [...sortedPinned, ...sortedUnpinned];

                notebookAdapter.setAll(draft.notebooks, sortedNotebooks);
              }),
            );
            await queryFulfilled;
            dispatch(api.util.invalidateTags(['exclusiveContents']));
          } catch {
            patchResult?.undo();
          }
        },
      }),
      invalidateNotebookLists: builder.mutation<null, void>({
        queryFn: () => ({ data: null }),
        invalidatesTags: [QUERIES.exclusiveContents],
      }),
    }),
  });

  return api;
};
